/**
 * Hyperliquid REST API Client
 *
 * Market data client using POST /info endpoint.
 * All HL REST calls are POST with JSON body — no GET endpoints.
 * Caches asset index mapping from meta for use by other layers.
 */

import { HL_MAINNET_API, HL_TESTNET_API } from "./constants";
import type {
  HLMeta,
  HLAssetInfo,
  HLMetaAndAssetCtxs,
  HLL2Book,
  HLCandle,
  HLRecentTrade,
  HLAllMids,
  HLPerpDex,
  HLPerpCategories,
  HLAccountState,
  HLOpenOrder,
  HLUserFill,
} from "./types";

// ============================================================================
// Error Handling
// ============================================================================

export class HyperliquidApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public requestType: string,
  ) {
    super(message);
    this.name = "HyperliquidApiError";
  }
}

// ============================================================================
// Client
// ============================================================================

export interface HyperliquidInfoClientOptions {
  testnet?: boolean;
  /** HIP-3 dex names for multi-dex asset resolution (e.g. ["xyz", "cash"]) */
  dexNames?: string[];
}

export class HyperliquidInfoClient {
  private baseUrl: string;
  private assetIndexMap = new Map<string, number>();
  private assetInfoMap = new Map<string, HLAssetInfo>();
  private assetCacheTimestamp = 0;
  private dexNames: string[];
  /** Maps HIP-3 dex name → asset ID offset (100000 + perp_dex_index * 10000) */
  private dexOffsetMap = new Map<string, number>();
  /** Inflight request deduplication — concurrent identical requests share one fetch */
  private inflightRequests = new Map<string, Promise<unknown>>();

  /** Asset cache TTL — 5 minutes matches useHLAssetMeta hook */
  private static readonly ASSET_CACHE_TTL_MS = 5 * 60 * 1000;

  constructor(options: HyperliquidInfoClientOptions = {}) {
    this.baseUrl = options.testnet ? HL_TESTNET_API : HL_MAINNET_API;
    this.dexNames = options.dexNames ?? [];
  }

  // ==========================================================================
  // Market Data Methods
  // ==========================================================================

  /**
   * Get metadata for all perpetual assets.
   * Also populates the internal asset index cache.
   * @param dex Optional HIP-3 dex name to scope to a builder-deployed dex
   */
  async getMeta(dex?: string): Promise<HLMeta> {
    const meta = await this.postInfo<HLMeta>({
      type: "meta",
      ...(dex ? { dex } : {}),
    });
    this.cacheAssets(meta, dex ? (this.dexOffsetMap.get(dex) ?? 0) : 0);
    return meta;
  }

  /**
   * Get mid prices for all assets.
   * Returns { "BTC": "69000.5", "ETH": "3500.2", ... }
   */
  async getAllMids(): Promise<HLAllMids> {
    return this.postInfo<HLAllMids>({ type: "allMids" });
  }

  /**
   * Get L2 order book for a coin.
   * @param coin Base asset name (e.g., "BTC")
   */
  async getL2Book(coin: string): Promise<HLL2Book> {
    return this.postInfo<HLL2Book>({ type: "l2Book", coin });
  }

  /**
   * Get candle snapshot for a coin.
   * @param coin Base asset name
   * @param interval Candle interval (e.g., "1m", "5m", "1h", "1d")
   * @param startTime Start timestamp in ms
   * @param endTime End timestamp in ms
   */
  async getCandleSnapshot(
    coin: string,
    interval: string,
    startTime: number,
    endTime: number,
  ): Promise<HLCandle[]> {
    const result = await this.postInfo<HLCandle[]>({
      type: "candleSnapshot",
      req: { coin, interval, startTime, endTime },
    });
    return Array.isArray(result) ? result : [];
  }

  /**
   * Get recent trades for a coin.
   * @param coin Base asset name
   */
  async getRecentTrades(coin: string): Promise<HLRecentTrade[]> {
    const result = await this.postInfo<HLRecentTrade[]>({
      type: "recentTrades",
      coin,
    });
    return Array.isArray(result) ? result : [];
  }

  /**
   * Fetch the HIP-3 perp annotation for a coin — a small bundle of human
   * metadata (category, description, displayName, keywords). Only defined
   * for builder-deployed HIP-3 assets; main HL perps return 500 / empty.
   */
  async getPerpAnnotation(coin: string): Promise<{
    category?: string;
    description?: string;
    displayName?: string;
    keywords?: string[];
  } | null> {
    try {
      return await this.postInfo({ type: "perpAnnotation", coin });
    } catch {
      return null;
    }
  }

  /**
   * Get meta + asset contexts in a single call.
   * Returns [meta, assetCtx[]] — each assetCtx corresponds to meta.universe[i].
   * @param dex Optional HIP-3 dex name to scope to a builder-deployed dex
   */
  async getMetaAndAssetCtxs(dex?: string): Promise<HLMetaAndAssetCtxs> {
    const result = await this.postInfo<HLMetaAndAssetCtxs>({
      type: "metaAndAssetCtxs",
      ...(dex ? { dex } : {}),
    });

    // Cache assets from meta portion
    if (Array.isArray(result)) {
      this.cacheAssets(result[0], dex ? (this.dexOffsetMap.get(dex) ?? 0) : 0);
    }

    return result;
  }

  /**
   * Get perp categories — array of [coin, category] tuples.
   * For HIP-3 builder dexes: [["birb:PENGU","meme"], ["birb:TOKEN","defi"]].
   * Returns [] for the main dex.
   */
  async getPerpCategories(): Promise<HLPerpCategories> {
    return this.postInfo<HLPerpCategories>({ type: "perpCategories" });
  }

  /**
   * Get list of all deployed HIP-3 perp DEXs.
   * The position of each dex in the array determines its perp_dex_index,
   * used to compute global asset IDs: 100000 + perp_dex_index * 10000 + index_in_meta.
   */
  async getPerpDexs(): Promise<HLPerpDex[]> {
    const result = await this.postInfo<HLPerpDex[]>({ type: "perpDexs" });
    return Array.isArray(result) ? result : [];
  }

  // ==========================================================================
  // Account Data Methods
  // ==========================================================================

  /**
   * Get clearinghouse state (positions, margin) for an address.
   */
  async getClearinghouseState(address: string): Promise<HLAccountState> {
    return this.postInfo<HLAccountState>({
      type: "clearinghouseState",
      user: address,
    });
  }

  /**
   * Get open orders for an address.
   */
  async getOpenOrders(address: string): Promise<HLOpenOrder[]> {
    const result = await this.postInfo<HLOpenOrder[]>({
      type: "openOrders",
      user: address,
    });
    return Array.isArray(result) ? result : [];
  }

  /**
   * Get user fills (trade history) for an address.
   */
  async getUserFills(address: string): Promise<HLUserFill[]> {
    const result = await this.postInfo<HLUserFill[]>({
      type: "userFills",
      user: address,
    });
    return Array.isArray(result) ? result : [];
  }

  // ==========================================================================
  // Asset Index Resolution
  // ==========================================================================

  /**
   * Get asset index by coin name. Required for order placement.
   * Returns undefined if coin not found — caller should call getMeta() first.
   */
  getAssetIndex(coin: string): number | undefined {
    return this.assetIndexMap.get(coin);
  }

  /**
   * Get asset info by coin name.
   */
  getAssetInfo(coin: string): HLAssetInfo | undefined {
    return this.assetInfoMap.get(coin);
  }

  /**
   * Ensure asset cache is populated and fresh (5-min TTL).
   * Fetches perpDexs first to build the offset map for HIP-3 asset IDs,
   * then loads main + HIP-3 dex metadata.
   *
   * HIP-3 global asset ID = 100000 + perp_dex_index * 10000 + index_in_meta
   */
  async ensureAssetCache(): Promise<void> {
    const now = Date.now();
    if (
      this.assetIndexMap.size === 0 ||
      now - this.assetCacheTimestamp > HyperliquidInfoClient.ASSET_CACHE_TTL_MS
    ) {
      // Build dex offset map before loading meta (so getMeta(dex) can use offsets)
      if (this.dexNames.length > 0) {
        try {
          const allDexs = await this.getPerpDexs();
          for (const dex of this.dexNames) {
            const perpDexIndex = allDexs.findIndex((d) => d?.name === dex);
            if (perpDexIndex >= 0) {
              this.dexOffsetMap.set(dex, 100000 + perpDexIndex * 10000);
            }
          }
        } catch {
          // perpDexs might fail on testnet or if API is down — proceed without offsets
        }
      }

      const promises: Promise<HLMeta>[] = [this.getMeta()];
      for (const dex of this.dexNames) {
        promises.push(this.getMeta(dex));
      }
      await Promise.all(promises);
    }
  }

  /**
   * Get mid price for a coin. Falls back to metaAndAssetCtxs for HIP-3 coins
   * that aren't in allMids.
   */
  async getMidPrice(coin: string): Promise<string | undefined> {
    const mids = await this.getAllMids();
    if (mids[coin]) return mids[coin];

    // HIP-3 coins aren't in allMids — get price from asset context
    const colonIdx = coin.indexOf(":");
    if (colonIdx >= 0) {
      const dex = coin.slice(0, colonIdx);
      try {
        const [meta, ctxs] = (await this.getMetaAndAssetCtxs(dex)) as [
          HLMeta,
          unknown[],
        ];
        const idx = meta.universe.findIndex(
          (a: HLAssetInfo) => a.name === coin,
        );
        if (idx >= 0 && ctxs[idx]) {
          const ctx = ctxs[idx] as { midPx?: string; markPx: string };
          return ctx.midPx || ctx.markPx;
        }
      } catch {
        // Fall through
      }
    }
    return undefined;
  }

  // ==========================================================================
  // Internal
  // ==========================================================================

  private async postInfo<T>(body: Record<string, unknown>): Promise<T> {
    const key = JSON.stringify(body);

    // Return existing inflight request if an identical one is already pending
    const inflight = this.inflightRequests.get(key);
    if (inflight) return inflight as Promise<T>;

    const url = `${this.baseUrl}/info`;
    const requestType =
      typeof body.type === "string" ? body.type : "unknown";

    // Retry on transient 5xx and network errors. Client (4xx) errors are never
    // retried — they won't succeed on retry. Each attempt backs off with
    // 250ms/750ms/1500ms + jitter. Total worst-case wait ≈ 2.5s before giving up.
    const MAX_ATTEMPTS = 3;
    const attemptOnce = async (attempt: number): Promise<T> => {
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: key,
        });
        if (!response.ok) {
          const err = new HyperliquidApiError(
            `Hyperliquid API error: ${response.status} ${response.statusText}`,
            response.status,
            requestType,
          );
          // Retry on 5xx / 429; surface 4xx immediately.
          if (
            (response.status >= 500 || response.status === 429) &&
            attempt < MAX_ATTEMPTS
          ) {
            const delay = 250 * 3 ** (attempt - 1) + Math.random() * 150;
            await new Promise((r) => setTimeout(r, delay));
            return attemptOnce(attempt + 1);
          }
          throw err;
        }
        return (await response.json()) as T;
      } catch (e) {
        // Network / fetch-level failures (DNS, TCP, CORS, aborts) are also
        // worth retrying. Only HyperliquidApiError already decides above.
        if (e instanceof HyperliquidApiError) throw e;
        if (attempt < MAX_ATTEMPTS) {
          const delay = 250 * 3 ** (attempt - 1) + Math.random() * 150;
          await new Promise((r) => setTimeout(r, delay));
          return attemptOnce(attempt + 1);
        }
        throw e;
      }
    };

    const request = attemptOnce(1).finally(() => {
      this.inflightRequests.delete(key);
    });

    this.inflightRequests.set(key, request);
    return request as Promise<T>;
  }

  /**
   * Cache asset name → global asset ID mapping.
   * @param meta The meta response (universe array)
   * @param assetIdOffset For HIP-3 dexes: 100000 + perp_dex_index * 10000. For main dex: 0.
   */
  private cacheAssets(meta: HLMeta, assetIdOffset = 0): void {
    meta.universe.forEach((asset: HLAssetInfo, index: number) => {
      this.assetIndexMap.set(asset.name, assetIdOffset + index);
      this.assetInfoMap.set(asset.name, asset);
    });
    this.assetCacheTimestamp = Date.now();
  }
}

// ============================================================================
// Standalone Functions (convenience, stateless)
// ============================================================================

const defaultClient = new HyperliquidInfoClient();

/**
 * Get meta + asset contexts with the default client.
 * Useful for one-off calls outside of hook lifecycle.
 */
export function getHLMetaAndAssetCtxs(): Promise<HLMetaAndAssetCtxs> {
  return defaultClient.getMetaAndAssetCtxs();
}

/** Get all mid prices with the default client. */
export function getHLAllMids(): Promise<HLAllMids> {
  return defaultClient.getAllMids();
}

/** Get L2 book with the default client. */
export function getHLL2Book(coin: string): Promise<HLL2Book> {
  return defaultClient.getL2Book(coin);
}
